/** @jsx jsx */
import { styled as styled2, css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { ColoredTag } from '@atlassian/jira-option-color-picker/src/colored-tag/index.tsx';
import { useColoredLabelsStore } from '@atlassian/jira-option-color-picker/src/colored-labels-store/index.tsx';
import type { CardLabelProps, ContainerProps } from './types.tsx';

export const CardLabel = (props: CardLabelProps) => {
	const { text, highlight } = props;
	const labelText = typeof text !== 'string' ? text.name : text;

	if (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
	) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [{ coloredLabelSuggestionsMap }] = useColoredLabelsStore();

		const labelName = labelText.trim();

		let labelColor = typeof text !== 'string' ? text.color : null;

		if (!labelColor) {
			if (!Object.prototype.hasOwnProperty.call(coloredLabelSuggestionsMap, labelName)) {
				labelColor = memoizeGetAutoGeneratedColorForLabels(labelName);
			} else {
				labelColor = coloredLabelSuggestionsMap[labelName];
			}
		}

		const { backgroundColor: color } = getColorMetadata(labelColor);

		return <ColoredTag labelName={labelName} color={color} />;
	}

	const isSelected = !!highlight && highlight.includes(labelText);

	if (expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)) {
		const { backgroundColor: borderColor } = getColorMetadata(
			memoizeGetAutoGeneratedColorForLabels(labelText),
		);

		return (
			<span
				css={tagBorderStyles}
				// eslint-disable-next-line jira/react/no-style-attribute, @typescript-eslint/consistent-type-assertions
				style={{ '--tag-border-color': borderColor } as React.CSSProperties}
			>
				<Tag text={labelText} />
			</span>
		);
	}

	if (isVisualRefreshEnabled()) {
		return <Tag text={labelText} color={isSelected ? 'blue' : 'standard'} />;
	}

	return (
		<Container isSelected={isSelected}>
			<Content>{labelText}</Content>
		</Container>
	);
};

CardLabel.defaultProps = {
	highlight: [],
};

export default CardLabel;

const tagBorderStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		backgroundColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: 'var(--tag-border-color)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span > a': {
		color: token('color.text'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span > a:hover': {
		color: token('color.text'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.span<ContainerProps>({
	display: 'inline-flex',
	height: '20px',
	marginTop: token('space.050'),
	marginRight: token('space.050'),
	marginBottom: token('space.050'),
	marginLeft: token('space.050'),
	padding: 0,
	position: 'relative',
	cursor: 'default',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: ContainerProps) =>
		props.isSelected
			? token('color.background.accent.blue.subtler')
			: token('color.background.neutral'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.isSelected ? token('color.text.accent.blue') : token('color.text')),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled2.span({
	maxWidth: '180px',
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
